import { mobx, observable, action, computed, runInAction } from 'mobx';
import TcFileResource from '../files/tcfile-resource';
import DashboardFilters from './dashboard-filters';
import CoordinatorResource from '../coordinators/coordinator-resource';
import _ from 'lodash';
import moment from 'moment';
import Notifications from '../utils/notification-helper';
import Cons from '../../isomorphic/cons';

export default class DashboardStore {
    tcFileResource;
    coordinatorResource;
    dashboardFilters;

    @observable pendingTransactions = [];
    @observable activeListings = [];
    @observable pendingInvoicedFiles = [];
    @observable coordinators = [];

    @observable newTransactionCounts = { today: 0, wtd: 0, mtd: 0, ytd: 0 };
    @observable newListingCounts = { today: 0, wtd: 0, mtd: 0, ytd: 0 };
    @observable closedFileCounts = { today: 0, wtd: 0, mtd: 0, ytd: 0 };

    @computed get totalPendingTransactions() {
        return this.pendingTransactions.length;
    }
    @computed get totalActiveListings() {
        return this.activeListings.length;
    }
    @computed get totalPendingInvoicedFiles() {
        return this.pendingInvoicedFiles.length;
    }

    @computed get pendingTransactionsPerTc() {
        const results = [];
        _.forEach(this.coordinators, tc => {
            const count = _.filter(this.pendingTransactions, transaction => {
                if (transaction.coordinatorId === tc.coordinatorId) return tc;
            }).length;

            if (!tc.disabled || (tc.disabled && count > 0)) {
                results.push({
                    coordinatorId: tc.coordinatorId,
                    name: tc.name,
                    count,
                    isDisabled: tc.disabled,
                });
            }
        });

        return results;
    }

    @computed get activeListingsPerTc() {
        const results = [];
        _.forEach(this.coordinators, tc => {
            const count = _.filter(this.activeListings, listing => {
                if (listing.coordinatorId === tc.coordinatorId) return tc;
            }).length;

            if (!tc.disabled || (tc.disabled && count > 0)) {
                results.push({
                    coordinatorId: tc.coordinatorId,
                    name: tc.name,
                    count,
                    isDisabled: tc.disabled,
                });
            }
        });

        return results;
    }

    constructor(appStore) {
        this.appStore = appStore;
        this.tcFileResource = new TcFileResource();
        this.coordinatorResource = new CoordinatorResource();
        this.dashboardFilters = new DashboardFilters();
    }

    @action.bound loadData() {
        this.loadPendingTransactions();
        this.loadActiveLisitings();

        const role = this.appStore.coordinatorDetailStore.coordinator.roles[0].name;
        if (role === 'tc') {
            this.loadClosedFileCountsForTC();
            this.loadNewTransactionCountsForTC();
            this.loadNewListingCountsForTC();
        } else {
            this.loadCoordinators();
            this.loadTotalPendingInvoicedFiles();
            this.loadClosedFileCounts();
            this.loadNewTransactionCounts();
            this.loadNewListingCounts();
        }
    }

    @action.bound loadPendingTransactions() {
        return this.tcFileResource
            .getFiles(
                this.appStore.authStore.getAccessToken(),
                this.dashboardFilters.filters.PendingTransactions.filter
            )
            .then(fileModels => {
                this.updatePendingTransactionsFiles(fileModels);
            });
    }

    @action.bound loadActiveLisitings() {
        return this.tcFileResource
            .getFiles(this.appStore.authStore.getAccessToken(), this.dashboardFilters.filters.ActiveLisitings.filter)
            .then(fileModels => {
                this.updateActiveListingsFiles(fileModels);
            });
    }

    @action.bound loadTotalPendingInvoicedFiles() {
        this.tcFileResource
            .getFiles(
                this.appStore.authStore.getAccessToken(),
                this.dashboardFilters.filters.TotalPendingInvoicedFiles.filter
            )
            .then(fileModels => {
                this.updateTotalPendingInvoicedFiles(fileModels);
            });
    }

    @action.bound loadNewTransactionCounts() {
        let newTransactionsPromises = [
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewTransactionCountsToday.filter
                    )
                    .then(x => {
                        resolve({ today: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewTransactionCountsWtd.filter
                    )
                    .then(x => {
                        resolve({ wtd: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewTransactionCountsMtd.filter
                    )
                    .then(x => {
                        resolve({ mtd: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewTransactionCountsYtd.filter
                    )
                    .then(x => {
                        resolve({ ytd: x });
                    });
            }),
        ];

        Promise.all(newTransactionsPromises).then(results => {
            let result = this._assignResults(results);
            this.updateNewTransactionCounts(result);
        });
    }

    @action.bound loadNewTransactionCountsForTC() {
        this.tcFileResource
            .getFilesCount(
                this.appStore.authStore.getAccessToken(),
                this.dashboardFilters.filters.NewTransactionCountsWtd.filter
            )
            .then(count => {
                this.updateNewTransactionCounts({ wtd: count });
            });
    }

    @action.bound loadNewListingCounts() {
        let listingPromises = [
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewListingCountsToday.filter
                    )
                    .then(x => {
                        resolve({ today: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewListingCountsWtd.filter
                    )
                    .then(x => {
                        resolve({ wtd: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewListingCountsMtd.filter
                    )
                    .then(x => {
                        resolve({ mtd: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.NewListingCountsYtd.filter
                    )
                    .then(x => {
                        resolve({ ytd: x });
                    });
            }),
        ];

        Promise.all(listingPromises).then(results => {
            let result = this._assignResults(results);
            this.updateNewListingCounts(result);
        });
    }

    @action.bound loadNewListingCountsForTC() {
        this.tcFileResource
            .getFilesCount(
                this.appStore.authStore.getAccessToken(),
                this.dashboardFilters.filters.NewListingCountsWtd.filter
            )
            .then(count => {
                this.updateNewListingCounts({ wtd: count });
            });
    }

    @action.bound loadClosedFileCounts() {
        let closedFilesPromises = [
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.ClosedFileCountsToday.filter
                    )
                    .then(x => {
                        resolve({ today: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.ClosedFileCountsWtd.filter
                    )
                    .then(x => {
                        resolve({ wtd: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.ClosedFileCountsMtd.filter
                    )
                    .then(x => {
                        resolve({ mtd: x });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.ClosedFileCountsYtd.filter
                    )
                    .then(x => {
                        resolve({ ytd: x });
                    });
            }),
        ];

        Promise.all(closedFilesPromises).then(results => {
            let result = this._assignResults(results);
            this.updateClosedFilesCounts(result);
        });
    }

    @action.bound loadClosedFileCountsForTC() {
        let promises = [
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.ClosedFileCountsMtd.filter
                    )
                    .then(count => {
                        resolve({ mtd: count });
                    });
            }),
            new Promise(resolve => {
                this.tcFileResource
                    .getFilesCount(
                        this.appStore.authStore.getAccessToken(),
                        this.dashboardFilters.filters.FilesClosingThisWeek.filter
                    )
                    .then(count => {
                        resolve({ closing: count });
                    });
            }),
        ];

        Promise.all(promises).then(results => {
            let result = this._assignResults(results);
            this.updateClosedFilesCounts(result);
        });
    }

    _assignResults(results) {
        let result = {};
        _.forEach(results, item => {
            result = Object.assign(result, item);
        });

        return result;
    }

    @action.bound loadCoordinators() {
        return this.coordinatorResource.getCoordinators(this.appStore.authStore.getAccessToken()).then(tcs => {
            this.updateCoordinators(tcs);
        });
    }

    @action updatePendingTransactionsFiles(fileModels) {
        this.pendingTransactions.clear();
        this.pendingTransactions = fileModels;
    }

    @action updateActiveListingsFiles(fileModels) {
        this.activeListings.clear();
        this.activeListings = fileModels;
    }

    @action updateTotalPendingInvoicedFiles(fileModels) {
        this.pendingInvoicedFiles.clear();
        this.pendingInvoicedFiles = fileModels;
    }

    @action updateCoordinators(tcs) {
        const coordinators = tcs.filter(coordinator => {
            return coordinator.roles.some(role => {
                return role.name !== Cons.roles.sales;
            });
        });
        this.coordinators.clear();
        this.coordinators = coordinators;
    }

    @action updateNewTransactionCounts(transactionCount) {
        this.newTransactionCounts = transactionCount; //Object.assign(this.newTransactionCounts,transactionCount)
    }
    @action updateNewListingCounts(listingCount) {
        this.newListingCounts = listingCount; // Object.assign(this.newListingCounts,listingCount)
    }
    @action updateClosedFilesCounts(closedFileCounts) {
        this.closedFileCounts = closedFileCounts; //Object.assign(this.closedFileCounts,closedFileCounts)
    }
}



// WEBPACK FOOTER //
// ./src/dashboard/dashboard-store.js